/**
 * @fileoverview Shared cities constant
 * Contains city data with coordinates for use across the application
 */

export const CITIES = {
  portland: { name: 'Portland', lat: 45.5152, lng: -122.6784 },
  newYork: { name: 'New York', lat: 40.7128, lng: -74.0060 },
  sanFrancisco: { name: 'San Francisco', lat: 37.7749, lng: -122.4194 },
  honolulu: { name: 'Honolulu', lat: 21.3099, lng: -157.8581 },
  tokyo: { name: 'Tokyo', lat: 35.6762, lng: 139.6503 },
} as const;

export type CityKey = keyof typeof CITIES;

export const CITY_OPTIONS = Object.entries(CITIES).map(([key, city]) => ({
  value: key,
  label: city.name,
}));

export type CityFilter = CityKey | "all";

const EARTH_RADIUS_MILES = 3959;

export function distanceMiles(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return EARTH_RADIUS_MILES * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function nearestCityKey(lat: number, lng: number, maxMiles = 30): CityKey | null {
  let best: { key: CityKey; d: number } | null = null;
  for (const key of Object.keys(CITIES) as CityKey[]) {
    const d = distanceMiles(lat, lng, CITIES[key].lat, CITIES[key].lng);
    if (!best || d < best.d) best = { key, d };
  }
  return best && best.d <= maxMiles ? best.key : null;
}

export function nearestCityName(lat: string | number, lng: string | number): string | null {
  const la = typeof lat === "string" ? parseFloat(lat) : lat;
  const ln = typeof lng === "string" ? parseFloat(lng) : lng;
  if (Number.isNaN(la) || Number.isNaN(ln)) return null;
  const key = nearestCityKey(la, ln);
  return key ? CITIES[key].name : null;
}
